import { useState } from "react";
import { motion } from "framer-motion";
import {
  Store, Search, ToggleLeft, ToggleRight, ClipboardList,
  DollarSign, Eye, Trash2, Loader2, ExternalLink, LogIn,
  CreditCard, Check
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

interface Props {
  restaurants: any[];
  setRestaurants: React.Dispatch<React.SetStateAction<any[]>>;
  orders: any[];
  plans: any[];
  logAction: (action: string, entityType: string, entityId: string, details?: any) => void;
  onImpersonate?: (restaurantId: string) => void;
}

const AdminRestaurantsTab = ({ restaurants, setRestaurants, orders, plans, logAction, onImpersonate }: Props) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [viewingRestaurant, setViewingRestaurant] = useState<any | null>(null);
  const [changingPlan, setChangingPlan] = useState(false);

  const toggleRestaurant = async (rest: any) => {
    const { error } = await supabase
      .from("restaurants")
      .update({ is_active: !rest.is_active })
      .eq("id", rest.id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      setRestaurants((prev) =>
        prev.map((r) => r.id === rest.id ? { ...r, is_active: !r.is_active } : r)
      );
      logAction(rest.is_active ? "restaurant_suspended" : "restaurant_reactivated", "restaurant", rest.id, { name: rest.name });
      toast({ title: rest.is_active ? "Restaurante suspenso" : "Restaurante reativado" });
    }
  };

  const [deleting, setDeleting] = useState<string | null>(null);

  const deleteRestaurant = async (rest: any) => {
    if (!confirm(`Excluir permanentemente "${rest.name}" e todos os dados relacionados? Esta ação não pode ser desfeita.`)) return;
    setDeleting(rest.id);
    try {
      const { data, error } = await supabase.functions.invoke("admin-manage-users", {
        body: { action: "delete_restaurant", restaurant_id: rest.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setRestaurants((prev) => prev.filter((r) => r.id !== rest.id));
      logAction("restaurant_deleted", "restaurant", rest.id, { name: rest.name });
      toast({ title: "Restaurante excluído com sucesso" });
    } catch (error: any) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  const changePlan = async (restaurantId: string, planId: string | null) => {
    setChangingPlan(true);
    const { error } = await supabase
      .from("restaurants")
      .update({ plan_id: planId })
      .eq("id", restaurantId);
    if (error) {
      toast({ title: "Erro ao alterar plano", description: error.message, variant: "destructive" });
    } else {
      setRestaurants(prev => prev.map(r => r.id === restaurantId ? { ...r, plan_id: planId } : r));
      if (viewingRestaurant?.id === restaurantId) {
        setViewingRestaurant((prev: any) => prev ? { ...prev, plan_id: planId } : prev);
      }
      const planName = plans.find((p: any) => p.id === planId)?.name || "Sem plano";
      const restName = restaurants.find(r => r.id === restaurantId)?.name || "";
      logAction("restaurant_plan_changed", "restaurant", restaurantId, { plan_name: planName, restaurant_name: restName });
      toast({ title: "Plano alterado!", description: `Plano atualizado para "${planName}".` });
    }
    setChangingPlan(false);
  };

  const getPlanName = (planId: string | null) => {
    if (!planId) return "Sem plano";
    return plans.find((p: any) => p.id === planId)?.name || "—";
  };

  const filteredRestaurants = restaurants.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.slug.toLowerCase().includes(search.toLowerCase()) ||
    (r.address_city || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar restaurante..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
          />
        </div>
        <p className="text-xs text-muted-foreground whitespace-nowrap">{filteredRestaurants.length} resultados</p>
      </div>

      {filteredRestaurants.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
          <Store className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>Nenhum restaurante encontrado</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-gradient-card shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left p-4 font-medium">Nome</th>
                  <th className="text-left p-4 font-medium hidden md:table-cell">Tipo</th>
                  <th className="text-left p-4 font-medium hidden lg:table-cell">Plano</th>
                  <th className="text-left p-4 font-medium hidden md:table-cell">Cidade</th>
                  <th className="text-left p-4 font-medium">Pedidos</th>
                  <th className="text-left p-4 font-medium hidden lg:table-cell">Faturamento</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredRestaurants.map((rest) => {
                  const restOrders = orders.filter((o: any) => o.restaurant_id === rest.id);
                  const restRevenue = restOrders.reduce((s: number, o: any) => s + Number(o.total), 0);
                  return (
                    <tr key={rest.id} className={`border-b border-border/50 hover:bg-secondary/30 ${!rest.is_active ? "opacity-60" : ""}`}>
                      <td className="p-4">
                        <div>
                          <p className="text-sm font-medium">{rest.name}</p>
                          <p className="text-[10px] text-muted-foreground">{rest.slug}</p>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <Badge variant="outline" className="text-[10px] capitalize">{rest.type}</Badge>
                      </td>
                      <td className="p-4 hidden lg:table-cell">
                        <Badge variant="secondary" className="text-[10px]">
                          <CreditCard className="h-3 w-3 mr-1" />
                          {getPlanName(rest.plan_id)}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">
                        {rest.address_city ? `${rest.address_city}/${rest.address_state}` : "—"}
                      </td>
                      <td className="p-4 text-sm">{restOrders.length}</td>
                      <td className="p-4 text-sm font-medium hidden lg:table-cell">
                        R$ {restRevenue.toFixed(2).replace(".", ",")}
                      </td>
                      <td className="p-4">
                        <Badge variant={rest.is_active ? "default" : "destructive"} className="text-[10px]">
                          {rest.is_active ? "Ativo" : "Suspenso"}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          {onImpersonate && (
                            <button onClick={() => onImpersonate(rest.id)} className="p-1.5 rounded-lg hover:bg-primary/10" title="Conectar à loja">
                              <LogIn className="h-4 w-4 text-primary" />
                            </button>
                          )}
                          <button onClick={() => setViewingRestaurant(rest)} className="p-1.5 rounded-lg hover:bg-secondary" title="Ver detalhes">
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button onClick={() => toggleRestaurant(rest)} className="p-1.5 rounded-lg hover:bg-secondary" title={rest.is_active ? "Suspender" : "Reativar"}>
                            {rest.is_active ? <ToggleRight className="h-4 w-4 text-success" /> : <ToggleLeft className="h-4 w-4 text-muted-foreground" />}
                          </button>
                          <button onClick={() => deleteRestaurant(rest)} disabled={deleting === rest.id} className="p-1.5 rounded-lg hover:bg-destructive/10 disabled:opacity-50" title="Excluir">
                            {deleting === rest.id ? <Loader2 className="h-4 w-4 animate-spin text-destructive" /> : <Trash2 className="h-4 w-4 text-destructive" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Restaurant Detail Dialog */}
      <Dialog open={!!viewingRestaurant} onOpenChange={() => setViewingRestaurant(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{viewingRestaurant?.name}</DialogTitle>
          </DialogHeader>
          {viewingRestaurant && (
            <div className="space-y-4">
              {/* Plan Change Section */}
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <Label className="text-sm font-semibold">Plano do Restaurante</Label>
                </div>
                <Select
                  value={viewingRestaurant.plan_id || "none"}
                  onValueChange={(value) => changePlan(viewingRestaurant.id, value === "none" ? null : value)}
                  disabled={changingPlan}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Selecione um plano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem plano</SelectItem>
                    {plans.filter((p: any) => p.is_active).map((plan: any) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} — R$ {Number(plan.price_monthly).toFixed(2).replace(".", ",")}/mês
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {changingPlan && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Atualizando plano...
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Slug", value: viewingRestaurant.slug },
                  { label: "Tipo", value: viewingRestaurant.type },
                  { label: "CNPJ", value: viewingRestaurant.cnpj || "—" },
                  { label: "Telefone", value: viewingRestaurant.phone || "—" },
                  { label: "Cidade", value: viewingRestaurant.address_city ? `${viewingRestaurant.address_city}/${viewingRestaurant.address_state}` : "—" },
                  { label: "CEP", value: viewingRestaurant.address_zip || "—" },
                  { label: "Horário", value: viewingRestaurant.opening_time ? `${viewingRestaurant.opening_time} - ${viewingRestaurant.closing_time}` : "—" },
                  { label: "Taxa Delivery", value: `R$ ${Number(viewingRestaurant.delivery_fee || 0).toFixed(2).replace(".", ",")}` },
                  { label: "Pedido Mínimo", value: `R$ ${Number(viewingRestaurant.min_order_value || 0).toFixed(2).replace(".", ",")}` },
                  { label: "Raio Delivery", value: `${viewingRestaurant.delivery_radius_km || 0} km` },
                  { label: "Cor Primária", value: viewingRestaurant.primary_color || "—" },
                  { label: "Criado em", value: new Date(viewingRestaurant.created_at).toLocaleDateString("pt-BR") },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-[10px] text-muted-foreground">{item.label}</p>
                    <p className="text-sm font-medium">{item.value}</p>
                  </div>
                ))}
              </div>
              {viewingRestaurant.description && (
                <div>
                  <p className="text-[10px] text-muted-foreground">Descrição</p>
                  <p className="text-sm">{viewingRestaurant.description}</p>
                </div>
              )}
              <div className="flex gap-2">
                {viewingRestaurant.social_instagram && (
                  <Badge variant="outline" className="text-[10px]">IG: {viewingRestaurant.social_instagram}</Badge>
                )}
                {viewingRestaurant.social_facebook && (
                  <Badge variant="outline" className="text-[10px]">FB: {viewingRestaurant.social_facebook}</Badge>
                )}
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-[10px] text-muted-foreground mb-1">Owner ID</p>
                <p className="text-xs font-mono text-muted-foreground">{viewingRestaurant.owner_id}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRestaurantsTab;
